import { useEffect, useState } from "react";
import type { DetailSource, MapProgress, PlaygroundRun } from "../types";
import { detailSourceFor } from "../utils/detailSource";
import { stripAnsi } from "../utils/parseOutputs";
import { GrepLinesView, GrepResultsView } from "./GrepResultsView";
import { MapResultsView } from "./MapResultsView";
import { SearchResultsView } from "./SearchResultsView";
import { DOT, LABEL, PANEL, PANEL_HEAD, choice } from "./styles";

type OutputView = "visual" | "terminal" | "json";

const ViewTabs = ({
  view,
  onChange,
}: {
  view: OutputView;
  onChange: (v: OutputView) => void;
}) => (
  <div role="tablist" aria-label="Output format" className="flex items-baseline gap-x-3">
    {(
      [
        { id: "visual", label: "Visual" },
        { id: "terminal", label: "Terminal" },
        { id: "json", label: "JSON" },
      ] as const
    ).map((tab, i) => (
      <span key={tab.id} className="flex items-baseline gap-x-3">
        {i > 0 && <span aria-hidden className={DOT}>·</span>}
        <button
          type="button"
          role="tab"
          aria-selected={view === tab.id}
          onClick={() => onChange(tab.id)}
          className={choice(view === tab.id).replace("text-base leading-6", "text-sm leading-5")}
        >
          {tab.label}
        </button>
      </span>
    ))}
  </div>
);

const EmptyState = () => (
  <div className="flex flex-1 flex-col items-center justify-center py-16 text-center">
    <p className="text-base italic leading-7 text-zinc-700">Nothing has run yet.</p>
    <p className="mt-2 max-w-sm text-sm leading-6 text-zinc-500">
      Search 11M+ papers plus FDA documents and trials, grep the full-text
      corpus, or map a question across a result set. Results appear here, in
      the same shape the CLI returns.
    </p>
  </div>
);

const LoadingState = ({
  command,
  mapProgress,
}: {
  command: string;
  mapProgress: MapProgress | null;
}) => (
  <div className="flex flex-1 flex-col items-center justify-center gap-3 py-16">
    <p className="text-base italic text-zinc-500">Running…</p>
    <code className="max-w-full truncate px-4 font-mono text-xs text-zinc-500">
      <span className="select-none">$ </span>
      {command}
    </code>
    {mapProgress && mapProgress.total > 0 && (
      <div className="w-full max-w-xs px-4">
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={mapProgress.total}
          aria-valuenow={mapProgress.completed + mapProgress.failed}
          className="hero-progress !w-full"
        >
          <div
            className="h-full bg-zinc-900 transition-[width] duration-500"
            style={{
              width: `${Math.round(((mapProgress.completed + mapProgress.failed) / mapProgress.total) * 100)}%`,
            }}
          />
        </div>
        <p className="mt-1.5 text-center font-mono text-2xs tabular-nums text-zinc-500">
          {mapProgress.completed + mapProgress.failed}/{mapProgress.total}{" "}
          papers read
          {mapProgress.failed > 0 && ` (${mapProgress.failed} failed)`}
          {" · "}
          {mapProgress.elapsed_s}s
        </p>
      </div>
    )}
  </div>
);

export const OutputPanel = ({
  run,
  running,
  runningCommand,
  mapProgress,
  error,
  activeDocId,
  onOpenPaper,
  onGrepPaper,
  onOpenProtein,
}: {
  run: PlaygroundRun | null;
  running: boolean;
  runningCommand: string | null;
  mapProgress: MapProgress | null;
  error: string | null;
  /** Scroll-synced document highlighted in the result-set minimap. */
  activeDocId?: string | null;
  onOpenPaper: (
    docId: string,
    source: DetailSource,
    grepPattern?: string,
    grepLineNumbers?: number[],
    grepIgnoreCase?: boolean,
  ) => void;
  onGrepPaper: (docId: string) => void;
  onOpenProtein: (accession: string) => void;
}) => {
  const [view, setView] = useState<OutputView>("visual");

  // A fresh run should always land on the visual tab first.
  useEffect(() => {
    if (run) setView("visual");
  }, [run]);

  const renderVisual = (r: PlaygroundRun) => {
    if (r.op === "search") {
      const papers = r.response.result_data?.papers ?? [];
      return (
        <SearchResultsView
          papers={papers}
          resultsId={r.response.result_data?.results_id ?? r.response.result_id}
          searchSource={r.searchSource ?? undefined}
          activeId={activeDocId}
          onOpenPaper={onOpenPaper}
          onGrepPaper={onGrepPaper}
          onOpenProtein={onOpenProtein}
        />
      );
    }
    if (r.op === "grep") {
      const pattern =
        r.grepVisual?.pattern ?? extractGrepPattern(r.displayCommand);
      const ignoreCase =
        r.grepVisual?.ignoreCase ??
        (/\s-i(?:\s|$)/.test(r.displayCommand) ||
          // Result-set (--from) and corpus (target `/papers/`) grep both match
          // case-insensitively on the backend even without an explicit -i.
          /\s--from\s/.test(r.displayCommand) ||
          /\s\/papers\/?\s*$/.test(r.displayCommand));
      if (!r.grep) {
        // Couldn't group by paper (single-paper grep, "No matches…") — show the
        // raw lines, still bolding the matched term.
        return (
          <GrepLinesView
            output={stripAnsi(r.response.output).trim()}
            pattern={pattern}
            ignoreCase={ignoreCase}
            fixedStrings={r.grepVisual?.fixedStrings}
            wholeWords={r.grepVisual?.wholeWords}
          />
        );
      }
      return (
        <GrepResultsView
          grep={r.grep}
          pattern={pattern}
          ignoreCase={ignoreCase}
          fixedStrings={r.grepVisual?.fixedStrings}
          wholeWords={r.grepVisual?.wholeWords}
          onOpenPaper={(paper) =>
            onOpenPaper(
              paper.documentId,
              "papers",
              pattern,
              paper.matches.flatMap((match) =>
                typeof match.lineNumber === "number" ? [match.lineNumber] : [],
              ),
              ignoreCase,
            )
          }
        />
      );
    }
    if (r.map) {
      return (
        <MapResultsView
          question={r.map.question}
          resultsId={r.map.resultsId}
          tasks={r.map.tasks}
          onOpenPaper={(docId) => {
            // Map over a protein result set — task IDs are accessions.
            if (r.searchSource === "proteins") {
              onOpenProtein(docId);
              return;
            }
            const source = detailSourceFor(docId, r.searchSource ?? undefined);
            if (source) onOpenPaper(docId, source);
          }}
        />
      );
    }
    return null;
  };

  return (
    <section aria-label="Playground output" className={`${PANEL} flex min-h-[24rem] flex-col`}>
      <div className={`${PANEL_HEAD} flex items-baseline gap-3`}>
        <h2 className={LABEL}>Output</h2>
        {run && !running && (
          <span className="font-mono text-2xs tabular-nums text-zinc-400">
            {run.response.exit_code === 0 ? "ok" : `exit ${run.response.exit_code}`}
            {" · "}
            {(run.response.elapsed_ms ?? run.elapsedMs).toLocaleString()} ms
          </span>
        )}
        <div className="ml-auto">
          {run && !running && <ViewTabs view={view} onChange={setView} />}
        </div>
      </div>

      <div className="mt-5 flex min-h-0 flex-1 flex-col overflow-y-auto">
        {running ? (
          <LoadingState command={runningCommand ?? ""} mapProgress={mapProgress} />
        ) : error ? (
          <div className="flex flex-1 flex-col items-center justify-center py-16">
            <p className="max-w-md text-center text-base leading-6 text-red-700" role="alert">{error}</p>
          </div>
        ) : !run ? (
          <EmptyState />
        ) : view === "terminal" ? (
          <pre className="pg-code flex-1 overflow-x-auto px-4 py-3 font-mono text-xs leading-6 text-zinc-700">
            <span className="select-none text-zinc-400">$ {run.displayCommand}{"\n\n"}</span>
            {stripAnsi(run.response.output).trim() || "(no output)"}
          </pre>
        ) : view === "json" ? (
          <pre className="pg-code flex-1 overflow-x-auto px-4 py-3 font-mono text-xs leading-6 text-zinc-700">
            {JSON.stringify(run.response.result_data ?? run.response, null, 2)}
          </pre>
        ) : (
          renderVisual(run)
        )}
      </div>
    </section>
  );
};

/** Recover the quoted grep pattern from the display command for highlighting. */
const extractGrepPattern = (displayCommand: string): string | undefined => {
  const m = displayCommand.match(/^(?:paperclip )?grep "((?:[^"\\]|\\.)*)"/);
  return m ? m[1].replace(/\\(.)/g, "$1") : undefined;
};
